import React, { useEffect, useMemo, useState } from "react";
import _ from "lodash";
import { withRouter } from "react-router";
import { useRecoilStateLoadable } from "recoil";
import { IAccessibilitySettings, IFontFamily } from "@interfaces/Interfaces";
import { accessibilityAtom } from "@state/State";
import {
  IonBackButton,
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonImg,
  IonItem,
  IonLabel,
  IonPage,
  IonRange,
  IonToggle,
  IonToolbar,
  RangeChangeEventDetail,
  useIonViewWillEnter,
  useIonViewWillLeave,
} from "@ionic/react";
import EvidenceContainer from "@components/evidence/EvidenceContainer";
import DataController from "@controllers/DataController";
import { EventRegister } from "react-native-event-listeners";
import AccessibilitySection from "@components/profile/AccessibilitySection";
import { Preferences } from "@capacitor/preferences";
import * as FirebaseService from "@controllers/FirebaseService";
import {
  Button_Audio_Play_White,
  Button_Audio_Stop,
  Button_Line_Decrease,
  Button_Line_Increase,
  Button_Text_decrease,
  Button_Text_increase,
  Button_Tick,
} from "@assets/images";
import {
  DEFAULT_FONT_SIZE,
  DEFAULT_LINE_HEIGHT,
  MAX_FONT_SIZE,
  MAX_LINE_HEIGHT,
  MIN_FONT_SIZE,
  MIN_LINE_HEIGHT,
} from "../../Constants";
import HeaderTitle from "@components/common/HeaderTitle";
import { Capacitor } from "@capacitor/core";
import classNames from "classnames";
import { IonRangeCustomEvent } from "@ionic/core";
import { useTextToSpeech } from "@hooks/accessibility/useTextToSpeech";
import { Alpha, Hue } from "@uiw/react-color";

const COLOR_FILTER_RADIUS = 16;
const COLOR_FILTER_HEIGHT = 17;
const COLOR_FILTER_WIDTH = "100%";

const DEBOUNCE_DELAY = 5; // In milliseconds

const getSpeechSpeedValue = (value: RangeChangeEventDetail["value"]): number | null => {
  if (typeof value === "number" && !Number.isNaN(value)) {
    return value;
  }

  return null;
};

const Accessibility: React.FC = () => {
  const { handleTTS, playing, stopTTS } = useTextToSpeech("accessibility-container");

  const [accessibilitySettings, setAccessibilitySettings] = useRecoilStateLoadable<IAccessibilitySettings | null>(
    accessibilityAtom
  );

  const [localSpeechSpeed, setLocalSpeechSpeed] = useState<number | undefined>(
    accessibilitySettings.contents?.textToSpeechSpeed
  );

  const [localColorFilterHue, setLocalColorFilterHue] = useState<number | undefined>(
    accessibilitySettings.contents?.colorFilter?.h
  );
  const [localColorFilterAlpha, setLocalColorFilterAlpha] = useState<number | undefined>(
    accessibilitySettings.contents?.colorFilter?.alpha
  );

  useEffect(() => {
    setLocalColorFilterHue(accessibilitySettings.contents?.colorFilter?.h);
    setLocalColorFilterAlpha(accessibilitySettings.contents?.colorFilter?.alpha);
  }, [accessibilitySettings.contents?.colorFilter?.h, accessibilitySettings.contents?.colorFilter?.alpha]);

  useEffect(() => {
    const speechSpeed = accessibilitySettings.contents?.textToSpeechSpeed;
    if (typeof speechSpeed === "number" && !Number.isNaN(speechSpeed)) {
      setLocalSpeechSpeed(speechSpeed);
    }
  }, [accessibilitySettings.contents?.textToSpeechSpeed]);

  useIonViewWillEnter(() => {
    EventRegister.emit("tab-bar/visibility-changed", false);
  });

  useIonViewWillLeave(() => {
    EventRegister.emit("tab-bar/visibility-changed", true);
  });

  useEffect(() => {
    return () => {
      stopTTS();
    };
  }, []);

  const debouncedSaveColorFilter = useMemo(
    () =>
      _.debounce(async (settings: IAccessibilitySettings) => {
        try {
          await saveAccessibilitySetting(settings, "color_filter");
        } catch (error) {
          console.log(error);
        }
      }, DEBOUNCE_DELAY),
    []
  );

  const debouncedSaveColorFilterOpacity = useMemo(
    () =>
      _.debounce(async (settings: IAccessibilitySettings) => {
        try {
          await saveAccessibilitySetting(settings, "color_filter_opacity");
        } catch (error) {
          console.log(error);
        }
      }, DEBOUNCE_DELAY),
    []
  );

  const saveAccessibilitySetting = async (settings: IAccessibilitySettings, setting: string): Promise<void> => {
    try {
      await FirebaseService.logEvent("accessibility_setting_changed", {
        setting,
      });

      // console.log(settings);

      await Preferences.set({
        key: "accessibilitySettings",
        value: JSON.stringify(settings),
      });
      setAccessibilitySettings(settings);
    } catch (error) {
      console.log(error);
    }
  };

  const decreaseFontSize = async (): Promise<void> => {
    try {
      let fontSizeChange = accessibilitySettings.contents?.fontSizeChange;

      if (fontSizeChange && fontSizeChange > MIN_FONT_SIZE) {
        fontSizeChange = fontSizeChange - 1;
      } else if (!fontSizeChange) {
        fontSizeChange = -1;
      } else {
        return;
      }

      const settings: IAccessibilitySettings = {
        ...accessibilitySettings.contents,
        fontSizeChange,
      };

      await saveAccessibilitySetting(settings, "font_size");
    } catch (error) {
      console.log(error);
    }
  };

  const increaseFontSize = async (): Promise<void> => {
    try {
      let fontSizeChange = accessibilitySettings.contents?.fontSizeChange;

      if (fontSizeChange && fontSizeChange < MAX_FONT_SIZE) {
        fontSizeChange = fontSizeChange + 1;
      } else if (!fontSizeChange) {
        fontSizeChange = 1;
      } else {
        return;
      }

      const settings: IAccessibilitySettings = {
        ...accessibilitySettings.contents,
        fontSizeChange,
      };

      await saveAccessibilitySetting(settings, "font_size");
    } catch (error) {
      console.log(error);
    }
  };

  const decreaseLineHeight = async (): Promise<void> => {
    try {
      let lineHeightChange = accessibilitySettings.contents?.lineHeightChange;

      if (lineHeightChange && lineHeightChange > MIN_LINE_HEIGHT) {
        lineHeightChange = lineHeightChange - 0.5;
      } else if (!lineHeightChange) {
        lineHeightChange = 1;
      } else {
        return;
      }

      const settings: IAccessibilitySettings = {
        ...accessibilitySettings.contents,
        lineHeightChange,
      };

      await saveAccessibilitySetting(settings, "line_height");
    } catch (error) {
      console.log(error);
    }
  };

  const increaseLineHeight = async (): Promise<void> => {
    try {
      let lineHeightChange = accessibilitySettings.contents?.lineHeightChange;

      if (lineHeightChange && lineHeightChange < MAX_LINE_HEIGHT) {
        lineHeightChange = lineHeightChange + 0.5;
      } else if (!lineHeightChange) {
        lineHeightChange = 2;
      } else {
        return;
      }

      const settings: IAccessibilitySettings = {
        ...accessibilitySettings.contents,
        lineHeightChange,
      };

      await saveAccessibilitySetting(settings, "line_height");
    } catch (error) {
      console.log(error);
    }
  };

  const changeFontFamily = async (fontFamily: string): Promise<void> => {
    try {
      const settings: IAccessibilitySettings = {
        ...accessibilitySettings.contents,
        fontFamily,
      };

      await saveAccessibilitySetting(settings, "font_family");
    } catch (error) {
      console.log(error);
    }
  };

  const toggleTextToSpeech = async (): Promise<void> => {
    try {
      const settings: IAccessibilitySettings = {
        ...accessibilitySettings.contents,
        textToSpeech: accessibilitySettings.contents?.textToSpeech ? false : true,
        textToSpeechSpeed: 1,
      };

      await saveAccessibilitySetting(settings, "text_to_speech");
    } catch (error) {
      console.log(error);
    }
  };

  const toggleUnderlineButtons = async (): Promise<void> => {
    try {
      const settings: IAccessibilitySettings = {
        ...accessibilitySettings.contents,
        underlineButtons: accessibilitySettings.contents?.underlineButtons ? false : true,
      };

      await saveAccessibilitySetting(settings, "underline_buttons");
    } catch (error) {
      console.log(error);
    }
  };

  const toggleBoldText = async (): Promise<void> => {
    try {
      const settings: IAccessibilitySettings = {
        ...accessibilitySettings.contents,
        boldText: accessibilitySettings.contents?.boldText ? false : true,
      };

      await saveAccessibilitySetting(settings, "bold_text");
    } catch (error) {
      console.log(error);
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const toggleColorFilter = async (): Promise<void> => {
    try {
      const settings: IAccessibilitySettings = {
        ...accessibilitySettings.contents,
        colorFilter: accessibilitySettings.contents?.colorFilter ? undefined : { h: 216, s: 100, v: 100, alpha: 0.5 },
      };

      await saveAccessibilitySetting(settings, "color_filter");
    } catch (error) {
      console.log(error);
    }
  };

  const onColorFilterOpacityChange = (color: { a: number }): void => {
    if (color.a > 0.75) return;

    setLocalColorFilterAlpha(color.a);

    const settings: IAccessibilitySettings = {
      ...accessibilitySettings.contents,
      colorFilter: {
        ...accessibilitySettings.contents?.colorFilter,
        alpha: color.a,
      },
    };

    debouncedSaveColorFilterOpacity(settings);
  };

  const onColorFilterChange = (color: { h: number }): void => {
    setLocalColorFilterHue(color.h);

    const settings: IAccessibilitySettings = {
      ...accessibilitySettings.contents,
      colorFilter: {
        ...accessibilitySettings.contents?.colorFilter,
        h: color.h,
      },
    };

    debouncedSaveColorFilter(settings);
  };

  const fontSizeUnset = (): boolean => {
    return typeof accessibilitySettings.contents?.fontSizeChange === "undefined";
  };

  const lineHeightUnset = (): boolean => {
    return typeof accessibilitySettings.contents?.lineHeightChange === "undefined";
  };

  const fontFamilyUnset = (): boolean => {
    return typeof accessibilitySettings.contents?.fontFamily === "undefined";
  };

  const getFontSizePercentage = (): number => {
    const fontSizeChange = accessibilitySettings.contents?.fontSizeChange || 0;

    return 100 + fontSizeChange * 10;
  };

  const getLineHeightPercentage = (): number => {
    const lineHeightChange = accessibilitySettings.contents?.lineHeightChange || 1.5;

    switch (lineHeightChange) {
      case 1:
        return 66;
      case 1.5:
        return 100;
      case 2:
        return 133;
      case 2.5:
        return 166;
      case 3:
        return 200;
      default:
        return 100;
    }
  };

  const onSpeechSpeedChange = async (event: IonRangeCustomEvent<RangeChangeEventDetail>): Promise<void> => {
    const speechSpeed = getSpeechSpeedValue(event.detail.value);
    if (speechSpeed === null) {
      return;
    }

    setLocalSpeechSpeed(speechSpeed);

    const settings: IAccessibilitySettings = {
      ...accessibilitySettings.contents,
      textToSpeechSpeed: speechSpeed,
    };

    await saveAccessibilitySetting(settings, "text_to_speech_speed");
  };

  const onSpeechSpeedDrag = (event: IonRangeCustomEvent<RangeChangeEventDetail>): void => {
    const speechSpeed = getSpeechSpeedValue(event.detail.value);
    if (speechSpeed !== null) {
      setLocalSpeechSpeed(speechSpeed);
    }
  };

  const formatSpeechSpeed = (speed: number): string => {
    const numericSpeed = Number(speed);
    if (Number.isNaN(numericSpeed)) {
      return "1.00x";
    }

    return `${numericSpeed.toFixed(2)}x`;
  };

  const showResetButton = (): boolean => {
    const fontSizeChanged = !(accessibilitySettings.contents?.fontSizeChange == DEFAULT_FONT_SIZE || fontSizeUnset());
    const lineHeightChanged = !(
      accessibilitySettings.contents?.lineHeightChange == DEFAULT_LINE_HEIGHT || lineHeightUnset()
    );
    const boldTextActive = accessibilitySettings.contents?.boldText;

    return fontSizeChanged || lineHeightChanged || boldTextActive;
  };

  const resetSettings = async (): Promise<void> => {
    try {
      const settings: IAccessibilitySettings = {
        ...accessibilitySettings.contents,
        fontSizeChange: DEFAULT_FONT_SIZE,
        lineHeightChange: DEFAULT_LINE_HEIGHT,
        boldText: false,
      };

      await saveAccessibilitySetting(settings, "reset");
    } catch (error) {
      console.log(error);
    }
  };

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar
          mode="ios"
          className="navBar"
          style={{
            maxWidth: DataController.isWeb() ? 980 : undefined,
            height: Capacitor.getPlatform() === "android" ? "54px" : "unset",
          }}
        >
          <IonButtons slot="start">
            <IonBackButton
              className="header-back-buttons"
              defaultHref="/profile"
              text={DataController.getBackIconText()}
              icon={DataController.getBackIconType()}
              style={{
                marginLeft: Capacitor.getPlatform() === "android" ? 8 : 0,
                "--icon-font-size": Capacitor.getPlatform() === "android" ? "24px" : "30px",
              }}
            />
          </IonButtons>
          <HeaderTitle>{"Accessibility"}</HeaderTitle>
          <IonButtons slot="end">
            {accessibilitySettings.contents?.textToSpeech && (
              <IonButton
                className="header-button"
                mode="ios"
                onClick={() => handleTTS()}
              >
                <IonImg
                  src={playing ? Button_Audio_Stop : Button_Audio_Play_White}
                  className="headerIcon"
                />
              </IonButton>
            )}
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent
        className="page-background"
        id="accessibility-container"
      >
        <EvidenceContainer style={{ paddingTop: 0 }}>
          <div className="pl-initial-safe-area-l pr-initial-safe-area-r">
            <AccessibilitySection
              title="Text options"
              showResetButton={showResetButton()}
              resetButtonClicked={() => resetSettings()}
            >
              <div className="accessibilitySectionButtonsRow">
                <IonItem
                  button
                  detail={false}
                  className="accessibilitySectionPercentageButton"
                  onClick={() => decreaseFontSize()}
                >
                  <div className="accessibilitySectionPercentageButtonInner">
                    <IonImg
                      src={Button_Text_decrease}
                      className="accessibilitySectionPercentageIcon"
                      alt="Decrease font size icon"
                    />
                  </div>
                </IonItem>
                <IonItem
                  button
                  detail={false}
                  disabled
                  className="accessibilitySectionPercentageButtonReset"
                >
                  <div className="accessibilitySectionPercentageButtonInner">
                    <div className="accessibilitySectionPercentageTextPlaceholder">{`${getFontSizePercentage()}%`}</div>
                  </div>
                </IonItem>
                <IonItem
                  button
                  detail={false}
                  className="accessibilitySectionPercentageButton"
                  onClick={() => increaseFontSize()}
                >
                  <div className="accessibilitySectionPercentageButtonInner">
                    <IonImg
                      src={Button_Text_increase}
                      className="accessibilitySectionPercentageIcon"
                      alt="Increase font size icon"
                    />
                  </div>
                </IonItem>
              </div>
              <div className="accessibilitySectionButtonsRow">
                <IonItem
                  button
                  detail={false}
                  className="accessibilitySectionPercentageButton"
                  onClick={() => decreaseLineHeight()}
                >
                  <div className="accessibilitySectionPercentageButtonInner">
                    <IonImg
                      src={Button_Line_Decrease}
                      className="accessibilitySectionPercentageIcon"
                      alt="Decrease line height icon"
                    />
                  </div>
                </IonItem>
                <IonItem
                  button
                  detail={false}
                  disabled
                  className="accessibilitySectionPercentageButtonReset"
                >
                  <div className="accessibilitySectionPercentageButtonInner">
                    <div className="accessibilitySectionPercentageTextPlaceholder">{`${getLineHeightPercentage()}%`}</div>
                  </div>
                </IonItem>
                <IonItem
                  button
                  detail={false}
                  className="accessibilitySectionPercentageButton"
                  onClick={() => increaseLineHeight()}
                >
                  <div className="accessibilitySectionPercentageButtonInner">
                    <IonImg
                      src={Button_Line_Increase}
                      className="accessibilitySectionPercentageIcon"
                      alt="Increase line height icon"
                    />
                  </div>
                </IonItem>
              </div>
              <IonItem
                button
                onClick={() => toggleBoldText()}
                className="accessibilitySectionButton"
                detail={false}
              >
                <div className="accessibilitySectionButtonContainer">
                  <div className="accessibilitySectionButtonInnerContainer">
                    <IonLabel className="accessibilitySectionButtonTitle w-full">{"Bold text"}</IonLabel>
                    <IonToggle
                      justify="end"
                      color="success"
                      checked={accessibilitySettings.contents?.boldText}
                      mode="ios"
                      aria-label="Bold text toggle"
                      onClick={() => toggleBoldText()}
                    />
                  </div>
                </div>
              </IonItem>
            </AccessibilitySection>
            <div className="accessibilityTextPreviewContainer mt-2">
              <div className="accessibilityTextPreviewContainerInner">
                <div className="accessibilityTextPreviewText">
                  {
                    "This text is to show the current text size you have set. Use the options above to increase or decrease the text size, as well as the line height."
                  }
                </div>
              </div>
            </div>
            <AccessibilitySection title="Fonts">
              <IonItem
                button
                onClick={() => changeFontFamily("Inter")}
                disabled={accessibilitySettings.contents?.fontFamily === IFontFamily.INTER || fontFamilyUnset()}
                className="accessibilitySectionButton"
                detail={false}
              >
                <div className="accessibilitySectionButtonContainer">
                  <div className="accessibilitySectionButtonInnerContainer">
                    <IonLabel
                      className={classNames("accessibilitySectionButtonTitleInter", {
                        "font-bold": accessibilitySettings.contents?.fontFamily === IFontFamily.INTER,
                      })}
                    >
                      {"Default (Inter)"}
                    </IonLabel>
                    {(accessibilitySettings.contents?.fontFamily === IFontFamily.INTER || fontFamilyUnset()) && (
                      <IonImg
                        src={Button_Tick}
                        className="accessibilitySettingsTick"
                        alt="Selected"
                      />
                    )}
                  </div>
                </div>
              </IonItem>
              <IonItem
                button
                onClick={() => changeFontFamily("Atkinson Hyperlegible")}
                disabled={
                  accessibilitySettings.contents?.fontFamily === IFontFamily.ATKINSON_HYPERLEGIBLE || fontFamilyUnset()
                }
                className="accessibilitySectionButton"
                detail={false}
              >
                <div className="accessibilitySectionButtonContainer">
                  <div className="accessibilitySectionButtonInnerContainer">
                    <IonLabel
                      className={classNames("accessibilitySectionButtonTitleInter", {
                        "font-bold": accessibilitySettings.contents?.fontFamily === IFontFamily.ATKINSON_HYPERLEGIBLE,
                      })}
                    >
                      {"Atkinson Hyperlegible"}
                    </IonLabel>
                    {accessibilitySettings.contents?.fontFamily === IFontFamily.ATKINSON_HYPERLEGIBLE && (
                      <IonImg
                        src={Button_Tick}
                        className="accessibilitySettingsTick"
                        alt="Selected"
                      />
                    )}
                  </div>
                </div>
              </IonItem>
              <IonItem
                button
                onClick={() => changeFontFamily("Comic Sans MS")}
                disabled={accessibilitySettings.contents?.fontFamily === IFontFamily.COMIC_SANS_MS || fontFamilyUnset()}
                className="accessibilitySectionButton"
                detail={false}
              >
                <div className="accessibilitySectionButtonContainer">
                  <div className="accessibilitySectionButtonInnerContainer">
                    <IonLabel
                      className={classNames("accessibilityFontFamilyComicSansMS w-full", {
                        "font-bold": accessibilitySettings.contents?.fontFamily === IFontFamily.COMIC_SANS_MS,
                      })}
                    >
                      {"Comic Sans MS"}
                    </IonLabel>
                    {accessibilitySettings.contents?.fontFamily === IFontFamily.COMIC_SANS_MS && (
                      <IonImg
                        src={Button_Tick}
                        className="accessibilitySettingsTick"
                        alt="Selected"
                      />
                    )}
                  </div>
                </div>
              </IonItem>
              <IonItem
                button
                disabled={accessibilitySettings.contents?.fontFamily === IFontFamily.OPEN_DYSLEXIC}
                onClick={() => changeFontFamily("Open Dyslexic")}
                className="accessibilitySectionButton"
                detail={false}
              >
                <div className="accessibilitySectionButtonContainer">
                  <div className="accessibilitySectionButtonInnerContainer">
                    <IonLabel
                      className={classNames("accessibilityFontFamilyDyslexic", {
                        "font-bold": accessibilitySettings.contents?.fontFamily === IFontFamily.OPEN_DYSLEXIC,
                      })}
                    >
                      {"OpenDyslexic"}
                    </IonLabel>
                    {accessibilitySettings.contents?.fontFamily === IFontFamily.OPEN_DYSLEXIC && (
                      <IonImg
                        src={Button_Tick}
                        className="accessibilitySettingsTick"
                        alt="Selected"
                      />
                    )}
                  </div>
                </div>
              </IonItem>
              <IonItem
                button
                onClick={() => changeFontFamily("Roboto")}
                disabled={accessibilitySettings.contents?.fontFamily === IFontFamily.ROBOTO || fontFamilyUnset()}
                className="accessibilitySectionButton"
                detail={false}
              >
                <div className="accessibilitySectionButtonContainer">
                  <div className="accessibilitySectionButtonInnerContainer">
                    <IonLabel
                      className={classNames("accessibilityFontFamilyRoboto w-full", {
                        "font-bold": accessibilitySettings.contents?.fontFamily === IFontFamily.ROBOTO,
                      })}
                    >
                      {"Roboto"}
                    </IonLabel>
                    {accessibilitySettings.contents?.fontFamily === IFontFamily.ROBOTO && (
                      <IonImg
                        src={Button_Tick}
                        className="accessibilitySettingsTick"
                        alt="Selected"
                      />
                    )}
                  </div>
                </div>
              </IonItem>
              <IonItem
                button
                onClick={() => changeFontFamily("Roboto Serif")}
                disabled={accessibilitySettings.contents?.fontFamily === IFontFamily.ROBOTO_SERIF || fontFamilyUnset()}
                className="accessibilitySectionButton"
                detail={false}
              >
                <div className="accessibilitySectionButtonContainer">
                  <div className="accessibilitySectionButtonInnerContainer">
                    <IonLabel
                      className={classNames("accessibilityFontFamilyRobotoSerif w-full", {
                        "font-bold": accessibilitySettings.contents?.fontFamily === IFontFamily.ROBOTO_SERIF,
                      })}
                    >
                      {"Roboto Serif"}
                    </IonLabel>
                    {accessibilitySettings.contents?.fontFamily === IFontFamily.ROBOTO_SERIF && (
                      <IonImg
                        src={Button_Tick}
                        className="accessibilitySettingsTick"
                      />
                    )}
                  </div>
                </div>
              </IonItem>
              <IonItem
                button
                onClick={() => changeFontFamily("Verdana")}
                disabled={accessibilitySettings.contents?.fontFamily === IFontFamily.VERDANA || fontFamilyUnset()}
                className="accessibilitySectionButton"
                detail={false}
              >
                <div className="accessibilitySectionButtonContainer">
                  <div className="accessibilitySectionButtonInnerContainer">
                    <IonLabel
                      className={classNames("accessibilityFontFamilyVerdana w-full", {
                        "font-bold": accessibilitySettings.contents?.fontFamily === IFontFamily.VERDANA,
                      })}
                    >
                      {"Verdana"}
                    </IonLabel>
                    {accessibilitySettings.contents?.fontFamily === IFontFamily.VERDANA && (
                      <IonImg
                        src={Button_Tick}
                        className="accessibilitySettingsTick"
                        alt="Selected"
                      />
                    )}
                  </div>
                </div>
              </IonItem>
            </AccessibilitySection>
            <AccessibilitySection title="Speech">
              <IonItem
                button
                onClick={() => toggleTextToSpeech()}
                className="accessibilitySectionButton"
                detail={false}
              >
                <div className="accessibilitySectionButtonContainer">
                  <div className="accessibilitySectionButtonInnerContainer flex justify-between">
                    <IonLabel className="accessibilitySectionButtonTitle w-full">{"Show text-to-speech"}</IonLabel>
                    <IonToggle
                      justify="end"
                      color="success"
                      checked={accessibilitySettings.contents?.textToSpeech ?? false}
                      mode="ios"
                      aria-label="Show text-to-speech"
                      onClick={() => toggleTextToSpeech()}
                    />
                  </div>
                </div>
              </IonItem>
              {/* {accessibilitySettings.contents?.textToSpeech && (
                <IonItem
                  detail={false}
                  className="accessibilitySectionButton"
                >
                  <div className="accessibilitySectionButtonContainer">
                    <div className="accessibilitySectionButtonInnerContainer">
                      <IonLabel className="accessibilitySectionButtonTitle !no-underline">{"Speech speed"}</IonLabel>
                      <IonRange
                        snaps
                        ticks={false}
                        min={0.25}
                        max={2}
                        step={0.05}
                        value={localSpeechSpeed ?? 1}
                        onIonChange={(event) => onSpeechSpeedChange(event)}
                        onIonInput={(event) => onSpeechSpeedDrag(event)}
                        className="mx-4"
                      />
                      <IonLabel className="accessibilitySectionButtonTitle min-w-[50px] text-right !no-underline">
                        {formatSpeechSpeed(localSpeechSpeed ?? 1)}
                      </IonLabel>
                    </div>
                  </div>
                </IonItem>
              )} */}
            </AccessibilitySection>
            <AccessibilitySection title="Appearance">
              <IonItem
                button
                onClick={() => toggleUnderlineButtons()}
                className="accessibilitySectionButton"
                detail={false}
              >
                <div className="accessibilitySectionButtonContainer">
                  <div className="accessibilitySectionButtonInnerContainer">
                    <IonLabel className="accessibilitySectionButtonTitle w-full">{"Underline buttons"}</IonLabel>
                    <IonToggle
                      justify="end"
                      color="success"
                      checked={accessibilitySettings.contents?.underlineButtons}
                      mode="ios"
                      aria-label="Underline buttons"
                      onClick={() => toggleUnderlineButtons()}
                    />
                  </div>
                </div>
              </IonItem>
              <div>
                <IonItem
                  button
                  onClick={() => toggleColorFilter()}
                  className="accessibilitySectionButton"
                  detail={false}
                >
                  <div className="accessibilitySectionButtonContainer">
                    <div className="accessibilitySectionButtonInnerContainer">
                      <IonLabel className="accessibilitySectionButtonTitle w-full">{"Colour filter"}</IonLabel>
                      <IonToggle
                        justify="end"
                        color="success"
                        checked={!!accessibilitySettings.contents?.colorFilter}
                        mode="ios"
                        aria-label="Colour filter"
                        onClick={() => toggleColorFilter()}
                      />
                    </div>
                  </div>
                </IonItem>
                {accessibilitySettings.contents?.colorFilter && (
                  <div className="flex flex-col gap-2 pb-4">
                    <div className="mt-4 flex w-full items-center gap-4 px-4">
                      <div className="accessibilitySectionButtonTitle !no-underline">{"Colour"}</div>
                      <Hue
                        height={COLOR_FILTER_HEIGHT}
                        width={COLOR_FILTER_WIDTH}
                        hue={localColorFilterHue}
                        radius={COLOR_FILTER_RADIUS}
                        onChange={(color) => onColorFilterChange(color)}
                      />
                    </div>
                    <div className="mt-4 flex w-full items-center gap-4 px-4">
                      <div className="accessibilitySectionButtonTitle !no-underline">{"Opacity"}</div>
                      <Alpha
                        height={COLOR_FILTER_HEIGHT}
                        width={COLOR_FILTER_WIDTH}
                        hsva={{
                          h: localColorFilterHue ?? 0,
                          s: 100,
                          v: 100,
                          a: localColorFilterAlpha ?? 0.5,
                        }}
                        radius={COLOR_FILTER_RADIUS}
                        onChange={(color) => onColorFilterOpacityChange(color)}
                      />
                    </div>
                  </div>
                )}
              </div>
            </AccessibilitySection>
          </div>
        </EvidenceContainer>
      </IonContent>
    </IonPage>
  );
};

export default withRouter(Accessibility);
